import { TableCell, TableRow } from "@mui/material";
import { useContext, type FC } from "react";
import { useTranslation } from "react-i18next";

import {
  formatDateDMY,
  NO_VALUE_DISPLAY,
  timezoneAdjustedDateToDayStartISO,
  useFormats,
  useTimezone,
  type IApprovalBookingConflict,
} from "@ryde/shared";

import { BulkPreviewStepConflictsContext } from "../bulk-preview-step-conflicts.context";
import { getOverlappingReasonTranslationKeyByBookingConflictType } from "../helpers";
import { RejectedBookingApprovalButtonTableCell } from "./rejected-booking-approval-button-table-cell";
import { RejectedBookingPassengerTableCell } from "./rejected-booking-passenger-table-cell";

interface IRejectedBookingsTableRowProps {
  conflictRow: IApprovalBookingConflict;
  /** Names the booking rather than the passenger, and lets this one row be approved on its own. */
  isApprovable?: boolean;
}

export const RejectedBookingsOverlappingRow: FC<
  IRejectedBookingsTableRowProps
> = ({ conflictRow, isApprovable }) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { timeFormat } = useFormats();
  const { isPreview } = useContext(BulkPreviewStepConflictsContext);
  const { bookingId, passengerId, passengerFullName, date, errorType, id } =
    conflictRow;

  return (
    <TableRow>
      <TableCell>
        {isApprovable ? (bookingId ?? NO_VALUE_DISPLAY) : passengerId}
      </TableCell>
      <RejectedBookingPassengerTableCell passengerId={passengerId}>
        {passengerFullName}
      </RejectedBookingPassengerTableCell>
      <TableCell>
        {getOverlappingReasonTranslationKeyByBookingConflictType({
          conflictRow,
          t,
          timeFormat,
          timezone,
        })}
      </TableCell>
      <TableCell>
        {formatDateDMY(
          timezoneAdjustedDateToDayStartISO(date, timezone),
          timezone,
        )}
      </TableCell>
      {isApprovable && isPreview && (
        <RejectedBookingApprovalButtonTableCell
          id={id}
          passengerId={passengerId}
          passengerFullName={passengerFullName}
          date={date}
          errorType={errorType}
          shouldApprove={conflict => conflict.id === id}
        />
      )}
    </TableRow>
  );
};
